import { ContentCopy } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useCustomSnackBar } from '../SnackBar/useCustomSnackBar';

type Props = {
  /** The text put on the clipboard. */
  value: string;
  /** What this copies, for a screen reader. Defaults to a bare "Copy". */
  label?: string;
  className?: string;
};

/**
 * Puts one value on the clipboard and says so.
 *
 * Lives here rather than in `gridUtils` because the order details panel needs it too, and a details
 * panel has no grid cell to hand it.
 */
export const CopyToClipboardButton: React.FC<Props> = ({ value, label, ...props }) => {
  const snackBar = useCustomSnackBar();
  const { t } = useTranslation();

  const handleClick = useCallback(
    async (event: React.MouseEvent<HTMLElement>) => {
      // A grid row opens the order when clicked, and this button sits inside one of its cells.
      event.stopPropagation();

      try {
        await navigator.clipboard.writeText(value);
        snackBar.showSuccess(t('global.copiedToClipboard'));
      } catch {
        // `writeText` rejects where the clipboard is not ours to write to — an insecure origin, or a
        // browser that allows it only within a user gesture it no longer believes we are in. Saying
        // nothing would leave a button that silently does nothing, and the value uncopied without the
        // reader knowing it.
        snackBar.showError(t('global.copyFailed'));
      }
    },
    [value, snackBar, t]
  );

  return (
    <StyledIconButton
      onClick={handleClick}
      size="small"
      color="primary"
      aria-label={label ?? t('global.copy')}
      {...props}
    >
      <ContentCopy />
    </StyledIconButton>
  );
};

const StyledIconButton = styled(IconButton)`
  padding: 0px;
`;
